// "Changes Requested" summary section. Lists every change the user has added
// across all categories (driver, named insured, address, vehicle, coverage,
// interested party) as expandable rows, matching the mockup: mode chip, title,
// missing-fields chip, Edit/Remove, and old -> new field values.

import React from "react";
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { SectionPanel } from "./SectionPanel";
import { IChangeEntry } from "./changesRequested.utils";

// Neutral, understated Edit/Remove buttons, per the mockup.
const neutralButtonSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: "0.8125rem",
  px: 1.5,
  py: 0.25,
  color: "text.primary",
  borderColor: "divider",
  "&:hover": {
    borderColor: "text.disabled",
    backgroundColor: "action.hover",
  },
};

const modeColor = (mode: string): "success" | "warning" | "error" => {
  switch (mode.toUpperCase()) {
    case "ADD":
      return "success";
    case "DELETE":
      return "error";
    default:
      return "warning";
  }
};

const ModeChip: React.FC<{ mode: string }> = ({ mode }) => {
  const color = modeColor(mode);
  return (
    <Box
      component="span"
      sx={(theme: any) => {
        const dark = theme.palette[color].dark || theme.palette[color].main;
        const contrast =
          theme.palette[color].contrastText || theme.palette.common.white;
        return {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 62,
          height: 22,
          px: 1.25,
          borderRadius: "11px",
          fontWeight: 700,
          fontSize: "0.68rem",
          lineHeight: 1,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          color: contrast,
          backgroundColor: dark,
          border: `1px solid ${dark}`,
        };
      }}
    >
      {mode.toUpperCase()}
    </Box>
  );
};

interface IChangesRequestedProps {
  entries: IChangeEntry[];
  disabled?: boolean;
  onEdit: (entry: IChangeEntry) => void;
  onRemove: (entry: IChangeEntry) => void;
}

export const ChangesRequested: React.FC<IChangesRequestedProps> = ({
  entries,
  disabled = false,
  onEdit,
  onRemove,
}) => {
  if (entries.length === 0) {
    return null;
  }

  return (
    <SectionPanel
      heading="Changes Requested"
      sx={{ mt: 3, mb: 1 }}
      bodySx={{ p: 0 }}
    >
      {entries.map((entry, idx) => (
        <Box key={entry.id}>
          {idx > 0 && <Divider />}
          <Box sx={{ px: 2.5, py: 1.75 }}>
            {/* Row header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              mb={1.5}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <ModeChip mode={entry.mode} />
                <Typography variant="body2" fontWeight={700}>
                  {entry.title}
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                {entry.missingCount > 0 && (
                  <Box
                    component="span"
                    sx={(theme: any) => {
                      const main = theme.palette.error.main;
                      return {
                        px: 1.25,
                        py: "2px",
                        borderRadius: "999px",
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        color: main,
                        backgroundColor: alpha(main, 0.1),
                      };
                    }}
                  >
                    {entry.missingCount}{" "}
                    {entry.missingCount === 1 ? "field" : "fields"} missing
                  </Box>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  disabled={disabled}
                  onClick={() => onEdit(entry)}
                  sx={neutralButtonSx}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  disabled={disabled}
                  onClick={() => onRemove(entry)}
                  sx={neutralButtonSx}
                >
                  Remove
                </Button>
              </Stack>
            </Stack>

            {/* Field rows (always visible, per mockup) */}
            <Box>
              {entry.fields.map((field, i) => (
                <Grid
                  container
                  key={`${entry.id}-field-${i}`}
                  spacing={1}
                  alignItems="center"
                  sx={{ py: 0.5 }}
                >
                  <Grid item xs={10} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      {field.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} md={1} sx={{ textAlign: "center" }}>
                    <Typography variant="body2" color="text.disabled">
                      →
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={field.missing ? "error.main" : "text.primary"}
                    >
                      {field.missing ? "Missing" : field.to || "—"}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        </Box>
      ))}
    </SectionPanel>
  );
};
